import styled from "@emotion/styled";
import { Flex, Panel } from "@xometry/xometry_loft";

const baseToolbarSpritePath = "/os/proposal/static/images/toolbarsprite.png";
const toolbarSpriteUrl = () => {
  const { hostname } = window.location;

  if (!hostname.endsWith("thomasnet.com")) {
    return baseToolbarSpritePath;
  }
  // In case the hoops viewer is being open from the thomasnet quote page,
  // load the static toolbar sprite file from Xometry.
  const isStageEnvironment = hostname.startsWith("stage");
  if (isStageEnvironment) {
    return `https://www.stage.xometry.net${baseToolbarSpritePath}`;
  }
  return `https://xometry.com${baseToolbarSpritePath}`;
};

const toolbarIcon = `
  background: url(${toolbarSpriteUrl()}) no-repeat top left;
  display: block;
  width: 32px;
  height: 32px;
  margin: 3px;
  cursor: pointer;
  z-index: 2;
`;

export interface IconButton {
  selected?: boolean;
  inverted?: boolean;
  disabled?: boolean;
}

interface IconProps extends IconButton {
  top?: string;
  index?: number;
}

export const HomeIcon = styled("i")`
  ${toolbarIcon}
  background-position: -89px -89px;

  &:hover {
    background-position: -131px -89px;
  }
`;

export const IconShaded = styled("i")`
  ${toolbarIcon}
  background-position: -257px -257px;

  &:hover {
    background-position: -299px -257px;
  }
`;

export const IconHiddenLine = styled("i")`
  ${toolbarIcon}
  background-position: -5px -89px;
`;

export const IconWireframeShaded = styled("i")`
  ${toolbarIcon}
  background-position: -341px -89px;

  &:hover {
    background-position: -341px -131px;
  }
`;

export const IconWireframe = styled("i")`
  ${toolbarIcon}
  background-position: -341px -5px;
`;

export const IconCameraPersp = styled("i")`
  ${toolbarIcon}
  background-position: -257px -173px;

  &:hover {
    background-position: -299px -173px;
  }
`;

export const IconCameraOrtho = styled("i")`
  ${toolbarIcon}
  background-position: -173px -173px;

  &:hover {
    background-position: -215px -173px;
  }
`;

export const IconCamera = styled("i")`
  ${toolbarIcon}
  background-position: -173px -5px;
`;

export const IconCameraBack = styled("i")`
  ${toolbarIcon}
  background-position: -5px -5px;

  &:hover {
    background-position: -47px -5px;
  }
`;

export const IconCameraBottom = styled("i")`
  ${toolbarIcon}
  background-position: -89px -5px;

  &:hover {
    background-position: -131px -5px;
  }
`;

export const IconCameraFront = styled("i")`
  ${toolbarIcon}
  background-position: -257px -47px;

  &:hover {
    background-position: -299px -47px;
  }
`;

export const IconCameraIso = styled("i")`
  ${toolbarIcon}
  background-position: -173px -89px;

  &:hover {
    background-position: -215px -89px;
  }
`;

export const IconCameraLeft = styled("i")`
  ${toolbarIcon}
  background-position: -257px -89px;

  &:hover {
    background-position: -299px -89px;
  }
`;

export const IconCameraRight = styled("i")`
  ${toolbarIcon}
  background-position: -5px -257px;

  &:hover {
    background-position: -47px -257px;
  }
`;

export const IconCameraTop = styled("i")`
  ${toolbarIcon}
  background-position: -89px -299px;

  &:hover {
    background-position: -131px -299px;
  }
`;

export const IconCuttingPlane = styled("i")`
  ${toolbarIcon}
  background-position: -5px -47px;
`;

export const IconMeasurementToolsEdgeLength = styled("i")<IconProps>`
  ${toolbarIcon}
  background-position: -173px -131px;
  ${props => {
    if (props.selected) return `background-color: #ddd;`;
  }}

  &:hover {
    background-position: -215px -131px;
  }
`;

export const IconMeasurementToolsFaceToFaceDistance = styled("i")<IconProps>`
  ${toolbarIcon}
  background-position: -89px -131px;
  ${props => {
    if (props.selected) return `background-color: #ddd;`;
  }}
  &:hover {
    background-position: -131px -131px;
  }
`;

export const IconMeasurementToolsFaceToFaceAngle = styled("i")<IconProps>`
  ${toolbarIcon}
  background-position: -5px -131px;
  ${props => {
    if (props.selected) return `background-color: #ddd;`;
  }}
  &:hover {
    background-position: -47px -131px;
  }
`;

export const IconRemoveAllMeasurements = styled("i")`
  ${toolbarIcon}
  background-position: -299px -341px;
`;

interface CuttingPlaneButtonProps {
  icon: string;
  element: IconButton;
}

export const CuttingPlaneButton = styled("i")<CuttingPlaneButtonProps>`
  ${toolbarIcon}
  ${props => {
    const { icon } = props;
    return `background-position: ${icon};`;
  }}

    ${props => {
    const { element } = props;
    let style = "";
    if (element.selected) {
      style = "background-color: #ddd;";
    } else if (element.inverted) {
      style = "background-color: #999;";
    }

    if (element.disabled) {
      style = "opacity: .5;";
    }
    return style;
  }}
`;

export const Popover = styled(Panel)<{ index: number }>`
  position: absolute;
  top: 0;
  left: 65px;
  z-index: 9;
  padding: 5px;
  border-radius: 6px;
  border: 1px solid rgba(0, 0, 0, 0.2);
  transform: translateY(calc(-50% + ${props => 48 * props.index}px + 12px));
`;

export const PopoverArrow = styled("div")<IconProps>`
  position: absolute;
  display: block;
  width: 15px;
  height: 15px;
  top: ${props => props.top};
  left: -8px;
  box-shadow: none;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-top: none;
  border-left: none;
  transform: rotate(135deg);
  background-color: white;

  z-index: 1;
`;

export const SubmenuRowTitle = styled("div")`
  display: flex;
  font-size: 0.875rem;
  height: 15px;
  padding-left: 5px;
  align-items: center;
  font-family: "Open Sans", sans-serif;
  font-size: 14px;
`;

export const StyledToolbar = styled("div")`
  position: absolute;
  top: 40px;
  left: 16px;
  padding: 3px;
  background-color: #fff;
  border: #999 solid 1px;
  border-radius: 5px;

  ul {
    display: flex;
    flex-direction: column;
    list-style: none;
    padding: 0;

    li {
      &:before {
        content: none;
      }

      i {
        margin: 5px;
        font-size: 32px;
      }
    }
  }
`;

export const NavItem = styled(Flex)`
  padding: 3px;
`;
